import React, { forwardRef } from "react";
import ContractingSimplifiedDTO from "features/contracting/dtos/ContractingSimplifiedDTO";
import DateFormatter from "features/shared/components/formatters/DateFormatter";
import { useTranslation } from "react-i18next";
import RoundButton from "features/shared/components/buttons/RoundButton";
import roundButtonStyles from "features/shared/components/buttons/roundbutton.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DropdownActionItem from "features/shared/components/dropdown/DropdownActionItem";
import { Dropdown } from "react-bootstrap";
import _ from "lodash";
import { formatDate } from "features/shared/helpers/Formatters";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";
import styles from "./contractingSimplified.module.scss";
import ContractingStateLabel from "./ContractingStateLabel";
import { getQueryParams } from "features/shared/helpers/queryParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import ContentDialogConfirm from "features/shared/components/contentDialog/ContentDialogConfirm";
import ContractingNoteSubDialog from "features/contracting/components/ContractingNoteSubDialog";
import { isContractingCancellable } from "features/contracting/helpers/contractingCancelHelper";
import { ContractingState } from "features/contracting/enums/contractingState";
import { useHistory } from "react-router-dom";

type Props = {
  contracting: ContractingSimplifiedDTO;
  forAdminUse?: boolean;
  onCancel?: (contractingId: string) => void;
  onNoteSave?: (contractingId: string, note: string) => void;
};

const ContractingSimplified = ({
  contracting,
  forAdminUse = false,
  onCancel,
  onNoteSave,
}: Props) => {
  // translations
  const tCustomerTypes = useTranslation("customer\\enums\\customerType").t;
  const tLabels = useTranslation("contracting\\contracting").t;

  // DI
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );

  // router
  const history = useHistory();

  // helpers
  const getCustomerInfo = () => {
    if (contracting.dateOfBirth) {
      return `${tLabels("contractingSimplified.dateOfBirth")} ${formatDate(
        contracting.dateOfBirth
      )}`;
    } else if (contracting.companyRegistrationNumber) {
      return `${tLabels("contractingSimplified.companyRegistrationNumber")} ${
        contracting.companyRegistrationNumber
      }`;
    }

    return "";
  };

  const getEffectiveDateClass = () => {
    return forAdminUse
      ? "d-none d-lg-block col"
      : "d-none d-sm-block col text-center text-lg-left";
  };

  // sub-renders
  const renderEffectiveDate = () => {
    if (contracting.contractExpectedEffectiveDate) {
      return (
        <>
          <span>{tLabels("contractingSimplified.from")} </span>
          <DateFormatter date={contracting.contractExpectedEffectiveDate} />
        </>
      );
    } else {
      return <>-</>;
    }
  };

  const renderCustomerCol = () => {
    if (forAdminUse) {
      return (
        <div className="d-none d-md-block col" style={{ minWidth: 0 }}>
          <div className="text-truncate">{contracting.customerName}</div>
          <div className="small text-muted">{getCustomerInfo()}</div>
        </div>
      );
    }
  };

  const renderCustomerType = () => {
    return (
      <div className="row no-gutters">
        <div className="col-auto">
          <div className={styles.label}>
            <CommodityIcon commodity={contracting.commodity} includeColor />
          </div>
        </div>
        <div className="col">
          <div className="text-truncate">
            {tCustomerTypes(contracting.customerType!)}
          </div>
        </div>
      </div>
    );
  };

  const renderPlaceOfDelivery = () => {
    return (
      <div className="row no-gutters align-items-center">
        <div className="col-auto">
          <div className={`${styles.label} small text-muted`}>
            {tLabels("contractingSimplified.placeOfDelivery")}:
          </div>
        </div>
        <div className="col" style={{ minWidth: 0 }}>{renderPlaceOfDeliveryIdentification()}</div>
      </div>
    );
  };

  const renderPlaceOfDeliveryIdentification = () => {
    if (contracting.placeOfDeliveryIdentification) {
      return (
        <div className="small text-secondary text-truncate">
          {contracting.placeOfDeliveryIdentification}
        </div>
      );
    } else {
      return <div className="small text-muted">-</div>;
    }
  };

  const renderButton = () => {
    if (forAdminUse) {
      return (
        <RoundButton
          asLink
          to={`/admin/contractings/${contracting.contractingId}`}
        />
      );
    } else {
      if (contracting.contractingState === ContractingState.ColectingData) {
        return (
          <RoundButton
            icon="edit"
            asLink
            to={{
              pathname: "/contracting/add",
              search: getQueryParams(
                "contractingId",
                contracting.contractingId!
              ).toString(),
            }}
          />
        );
      } else {
        return (
          <RoundButton
            asLink
            to={`/contracting/${contracting.contractingId}`}
          />
        );
      }
    }
  };

  const getNotePreview = (): string => {
    const note = contracting.note?.trim();

    if (!note) {
      return "-";
    }

    return note;
  };

  const openNoteDialog = () => {
    dialogService.openDialog(
      tLabels("contractingAdminActions.editNoteTitle"),
      () => (
        <div className="p-4">
          <ContractingNoteSubDialog
            initialNote={contracting.note ?? ""}
            onSave={(note) => {
              onNoteSave?.(contracting.contractingId!, note);
              dialogService.closeDialog();
            }}
            onCancel={() => dialogService.closeDialog()}
          />
        </div>
      )
    );
  };

  const openCancelConfirm = () => {
    dialogService.openDialog(
      tLabels("contractingAdminActions.cancelConfirmTitle"),
      () => (
        <ContentDialogConfirm
          message={tLabels("contractingAdminActions.cancelConfirmMessage")}
          action={() => onCancel?.(contracting.contractingId!)}
        />
      )
    );
  };

  const CustomToggle = forwardRef((props: any, ref: any) => (
    <button
      ref={ref}
      className={`btn btn-sm ${roundButtonStyles.roundBtn}`}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  ));

  const renderAdminActions = () => {
    const canCancel = isContractingCancellable(contracting.contractingState);
    const detailLink =
      contracting.contractingState === ContractingState.CollectingDataByAdmin
        ? {
            pathname: "/contracting/add",
            search: getQueryParams(
              "contractingId",
              contracting.contractingId!
            ).toString(),
          }
        : `/admin/contractings/${contracting.contractingId}`;

    return (
      <div className="d-flex flex-column flex-sm-row">
        <RoundButton asLink to={detailLink} />
        <div className="mt-2 mt-sm-0 ml-sm-2">
          <Dropdown alignRight>
            <Dropdown.Toggle
              id={`contracting-actions-${contracting.contractingId}`}
              as={CustomToggle as any}
            >
              <FontAwesomeIcon icon="cog" />
            </Dropdown.Toggle>
            <Dropdown.Menu alignRight>
              <DropdownActionItem
                title={tLabels("contractingAdminActions.editNote")}
                icon="comment"
                onClick={openNoteDialog}
              />
              {canCancel && (
                <>
                  <Dropdown.Divider />
                  <DropdownActionItem
                    title={tLabels("contractingAdminActions.cancel")}
                    icon="ban"
                    onClick={openCancelConfirm}
                    color="danger"
                  />
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    );
  };

  // render
  return (
    <div className="row no-gutters align-items-center">
      <div className="col">
        <div className="row no-gutters align-items-center">
          <div className="col-7 col-sm pr-3">
            {renderCustomerType()}
            {renderPlaceOfDelivery()}
          </div>
          {renderCustomerCol()}
          <div className={getEffectiveDateClass()}>{renderEffectiveDate()}</div>
          <div className="d-none d-lg-block col">
            <DateFormatter date={contracting.createdDate!} />
          </div>
          <div className="col-5 col-sm">
            <div className="d-inline-flex flex-column mw-100">
              <ContractingStateLabel
                state={contracting.contractingState!}
                textType={forAdminUse ? "admin" : "customer"}
              />
              <div className="d-lg-none small text-muted mt-1">
                <DateFormatter date={contracting.createdDate!} />
              </div>
            </div>
          </div>
          {forAdminUse && (
            <div className="d-none d-xl-block col" style={{ minWidth: 0 }}>
              <div
                className="small text-muted text-truncate"
                title={contracting.note || undefined}
              >
                {getNotePreview()}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="col-auto pl-3">
        {forAdminUse ? renderAdminActions() : renderButton()}
      </div>
    </div>
  );
};

export default ContractingSimplified;
